import React from "react";
import { useTranslation } from "react-i18next";
import { Check, X, FileText, ListChecks, Clock, RefreshCw, ExternalLink, Info } from "lucide-react";
import { COUNTRY_FACTS } from "@/lib/countryFacts";

const Card = ({ title, Icon, children }) => (
  <div className="rounded-2xl border border-border bg-card p-7 card-soft">
    <h3 className="font-heading text-xl text-primary flex items-center gap-2"><Icon className="h-5 w-5 text-orange-600" /> {title}</h3>
    <div className="mt-4">{children}</div>
  </div>
);

export default function CountryFacts({ slug, facts }) {
  const { t } = useTranslation();
  const f = facts || COUNTRY_FACTS[slug];
  if (!f) return null;
  // Faithful per-country translations from the i18n store; English source is the fallback.
  const ft = (key, fallback) => t(`country.${slug}.facts.${key}`, { defaultValue: fallback });
  const hasTop = f.decisions?.length > 0 || f.documents?.length > 0 || f.includes?.length > 0 || f.excludes?.length > 0;
  const hasBottom = f.stages?.length > 0 || f.ongoing?.length > 0;
  if (!f.suits && !f.govFee && !hasTop && !hasBottom) return null;
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-4" data-testid="country-facts">
      {f.suits && (
        <div className="max-w-3xl mb-8">
          <h2 className="font-heading text-2xl sm:text-3xl text-primary">{t("country_ui.factsSuits")}</h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">{ft("suits", f.suits)}</p>
        </div>
      )}

      {f.govFee && (
        <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 mb-8 flex items-start gap-3" data-testid="country-govfee">
          <Info className="h-4 w-4 text-orange-700 mt-0.5 shrink-0" />
          <p className="text-sm text-orange-900">{ft("govFee", f.govFee)}</p>
        </div>
      )}

      {hasTop && (
        <div className="grid lg:grid-cols-2 gap-6">
          {f.decisions?.length > 0 && (
            <Card title={t("country_ui.factsDecisions")} Icon={ListChecks}>
              <ul className="space-y-2.5">{f.decisions.map((d, i) => <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700"><span className="grid place-items-center h-4 w-4 mt-0.5 rounded-full bg-orange-100 shrink-0 font-mono text-[10px] text-orange-700">{i + 1}</span>{ft(`decisions.${i + 1}`, d)}</li>)}</ul>
            </Card>
          )}
          {f.documents?.length > 0 && (
            <Card title={t("country_ui.factsDocuments")} Icon={FileText}>
              <ul className="space-y-2.5">{f.documents.map((d, i) => <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700"><FileText className="h-4 w-4 mt-0.5 text-primary shrink-0" />{ft(`documents.${i + 1}`, d)}</li>)}</ul>
            </Card>
          )}
          {f.includes?.length > 0 && (
            <Card title={t("country_ui.factsIncludes")} Icon={Check}>
              <ul className="space-y-2.5">{f.includes.map((d, i) => <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700"><span className="grid place-items-center h-4 w-4 mt-0.5 rounded-full bg-green-100 shrink-0"><Check className="h-3 w-3 text-green-700" /></span>{ft(`includes.${i + 1}`, d)}</li>)}</ul>
            </Card>
          )}
          {f.excludes?.length > 0 && (
            <Card title={t("country_ui.factsExcludes")} Icon={X}>
              <ul className="space-y-2.5">{f.excludes.map((d, i) => <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700"><span className="grid place-items-center h-4 w-4 mt-0.5 rounded-full bg-red-100 shrink-0"><X className="h-3 w-3 text-red-600" /></span>{ft(`excludes.${i + 1}`, d)}</li>)}</ul>
            </Card>
          )}
        </div>
      )}

      {hasBottom && (
        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          {f.stages?.length > 0 && (
            <Card title={t("country_ui.factsStages")} Icon={Clock}>
              <ol className="space-y-3">{f.stages.map((s, i) => <li key={i} className="flex items-start gap-3 text-sm text-slate-700"><span className="grid place-items-center h-6 w-6 rounded-full bg-primary text-primary-foreground font-mono text-xs shrink-0">{i + 1}</span>{ft(`stages.${i + 1}`, s)}</li>)}</ol>
              {f.timelineNote && <p className="mt-4 text-xs text-muted-foreground border-t border-border pt-3"><Clock className="h-3.5 w-3.5 inline mr-1 text-orange-600" />{ft("timelineNote", f.timelineNote)}</p>}
            </Card>
          )}
          {f.ongoing?.length > 0 && (
            <Card title={t("country_ui.factsOngoing")} Icon={RefreshCw}>
              <ul className="space-y-2.5">{f.ongoing.map((d, i) => <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700"><RefreshCw className="h-4 w-4 mt-0.5 text-primary shrink-0" />{ft(`ongoing.${i + 1}`, d)}</li>)}</ul>
              {f.refs?.length > 0 && (
                <div className="mt-4 border-t border-border pt-3 space-y-1.5">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{t("country_ui.factsRefs")}</p>
                  {f.refs.map((r, i) => <a key={i} href={r.href} target="_blank" rel="noreferrer noopener" className="flex items-center gap-1.5 text-sm text-orange-700 hover:underline" data-testid={`country-ref-${i}`}>{ft(`refs.${i + 1}`, r.label)} <ExternalLink className="h-3.5 w-3.5" /></a>)}
                </div>
              )}
            </Card>
          )}
        </div>
      )}
    </section>
  );
}
